import React from 'react';
import { motion } from 'framer-motion';
import { HardHat, Award, TrendingUp, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
const benefits = [{
  icon: HardHat,
  title: 'Built for the Jobsite',
  description: 'Durable workwear that stands up to real use'
}, {
  icon: Award,
  title: 'Consistent Branding',
  description: 'Professional appearance across your entire crew'
}, {
  icon: TrendingUp,
  title: 'Scalable Programs',
  description: 'Easy reorders as your team grows'
}];
const faqs = [{
  question: 'What makes your workwear different?',
  answer: 'We focus on durability and consistency. Our workwear is built for crews who actually wear their gear on the jobsite, not just for display. We use proven decoration methods that hold up to repeated washing and real-world use.'
}, {
  question: 'Can you handle bulk orders for large crews?',
  answer: 'Yes. We support orders of any size, from small crews to large operations. Our process is built to scale, and we keep your specifications on file for easy reorders.'
}, {
  question: 'How do you ensure logo consistency across orders?',
  answer: 'We keep your artwork and specifications on file. Every reorder matches your previous order exactly, ensuring consistent branding across your entire team.'
}, {
  question: 'Do you offer long-term apparel programs?',
  answer: 'Yes. We work with contractors and businesses to set up ongoing apparel programs that support new hires, replacements, and seasonal needs. Contact us to discuss your specific requirements.'
}, {
  question: 'What types of workwear do you offer?',
  answer: 'We offer t-shirts, polos, hoodies, jackets, hats, and other workwear essentials. We can source specific items based on your needs and help you choose the right options for your crew.'
}, {
  question: 'How long does production take?',
  answer: 'Most workwear orders are completed within 7-10 business days after artwork approval. Rush production is available for time-sensitive projects.'
}];
export function BusinessWorkwear() {
  return <div className="min-h-screen bg-atomic-black" data-id="element-593">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden bg-gradient-to-br from-atomic-charcoal via-atomic-black to-atomic-black" data-id="element-594">
        <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'linear-gradient(rgba(163, 230, 53, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(163, 230, 53, 0.1) 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }} data-id="element-595" />
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-596">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="max-w-3xl mx-auto text-center" data-id="element-597">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-598">
              Business Workwear for{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-atomic-green to-lime-400" data-id="element-599">
                Contractors & Crews
              </span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8" data-id="element-600">
              Durable, professional workwear for contractors, trades, and crews
              across Michigan. Built for visibility, consistency, and real-world
              use.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-601">
              <a href="/contact" data-id="element-602">
                <Button size="lg" data-id="element-603">
                  Request Workwear Quote
                  <ArrowRight className="ml-2 w-4 h-4" data-id="element-604" />
                </Button>
              </a>
              <a href="/catalog" data-id="element-605">
                <Button variant="outline" size="lg" data-id="element-606">
                  View Options
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-atomic-black border-t border-white/5" data-id="element-607">
        <div className="container mx-auto px-4 md:px-6" data-id="element-608">
          <div className="max-w-5xl mx-auto" data-id="element-609">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-610">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-611">
                Why Contractors Choose Us
              </h2>
              <p className="text-lg text-gray-300" data-id="element-612">
                Reliability, durability, and consistent results
              </p>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-8" data-id="element-613">
              {benefits.map((benefit, index) => <motion.div key={benefit.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="bg-atomic-charcoal rounded-xl p-6 border border-white/10" data-id="element-614">
                  <div className="w-12 h-12 rounded-lg bg-atomic-green/10 flex items-center justify-center text-atomic-green mb-4 border border-atomic-green/20" data-id="element-615">
                    <benefit.icon size={24} data-id="element-616" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-617">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-618">
                    {benefit.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-atomic-charcoal border-t border-white/5" data-id="element-619">
        <div className="container mx-auto px-4 md:px-6" data-id="element-620">
          <div className="max-w-3xl mx-auto" data-id="element-621">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-622">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-623">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-300" data-id="element-624">
                Common questions about business workwear
              </p>
            </motion.div>
            <div className="space-y-6" data-id="element-625">
              {faqs.map((faq, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} className="bg-atomic-black rounded-xl p-6 border border-white/10" data-id="element-626">
                  <h3 className="text-lg font-bold text-white mb-3" data-id="element-627">
                    {faq.question}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-628">{faq.answer}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
    </div>;
}